import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useTSQItem,
  useTSMCreateItem,
  useTSMUpdateItem,
} from "../../hooks/useTSItems";
import { useTSQUOMTypeList, useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQLocationList } from "../../hooks/useTSLocations";
import {
  ItemCreate,
  ItemUpdate,
  ITEM_TYPES,
  ItemType,
} from "../../types/itemTypes";
import { UOMUnitFull } from "../../types/uomTypes";
import stringUtils from "../../utils/stringUtils";

// Validation schema for item form
const itemSchema = yup.object({
  number: yup
    .string()
    .required("Part number is required")
    .min(1, "Part number must be at least 1 character")
    .max(50, "Part number must be at most 50 characters"),
  name: yup
    .string()
    .required("Item name is required")
    .min(1, "Item name must be at least 1 character")
    .max(255, "Item name must be at most 255 characters"),
  item_type: yup.string().required("Item Type is required").oneOf(ITEM_TYPES),
  uom_type_id: yup.string().required("UOM Type is required"),
  purchase_uom_id: yup.string().required("Purchase UOM is required"),
  storage_uom_id: yup.string().required("Storage UOM is required"),
  sales_uom_id: yup.string().required("Sales UOM is required"),
  preferred_bin_id: yup.string().required("Preferred BIN is required"),
  default_purchase_price: yup
    .number()
    .min(0, "Standard cost must be positive")
    .optional(),
  default_sales_price: yup
    .number()
    .min(0, "List price must be positive")
    .optional(),
  track_lots: yup.boolean().optional(),
  item_expires: yup.boolean().optional(),
  item_requires_inspection: yup.boolean().optional(),
  minimum_stock: yup
    .number()
    .min(0, "Minimum stock must be positive")
    .optional(),
  maximum_stock: yup
    .number()
    .min(0, "Maximum stock must be positive")
    .optional(),
  notes: yup.string().optional(),
  description: yup.string().optional(),
});

export const PageItemForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const isViewMode = formMode === "view";
  const isEditMode = Boolean(id) && formMode === "edit";
  const isCreateMode = !id && formMode === "create";

  const {
    data: itemData,
    isLoading: loadingItem,
    error: itemError,
  } = useTSQItem(id || "");

  const { data: uomTypesResponse, isLoading: loadingUOMTypes } =
    useTSQUOMTypeList({
      limit: 10000,
      filters: [[]],
    });

  const { data: uomUnitsResponse, isLoading: loadingUOMUnits } =
    useTSQUOMUnitList({
      limit: 10000,
      filters: [[]],
    });

  const { data: locsResponse, isLoading: locsLoading } = useTSQLocationList({
    filters: [
      [
        { field: "location_type", operator: "==", value: "BIN" },
        { field: "top_location_name", operator: "==", value: "Omaha" },
      ],
    ],
  });

  const createItemMutation = useTSMCreateItem();
  const updateItemMutation = useTSMUpdateItem();

  const uomTypes = uomTypesResponse?.items || [];
  const uomUnits = uomUnitsResponse?.items || [];
  const locs = useMemo(() => {
    if (locsResponse?.items) {
      return locsResponse.items;
    } else {
      return [];
    }
  }, [locsResponse]);
  const loadingUOM = loadingUOMTypes || loadingUOMUnits;

  // const [loading, setLoading] = useState(false);
  // const [loadingItem, setLoadingItem] = useState(isEditMode || isViewMode);
  // const [loadingUOM, setLoadingUOM] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [itemData, setItemData] = useState<Item | null>(null);
  // const [uomTypes, setUomTypes] = useState<UOMTypeRead[]>([]);
  // const [uomUnits, setUomUnits] = useState<UOMUnitSummary[]>([]);
  const [selectedUomTypeId, setSelectedUomTypeId] = useState<string>("");
  const [availUomUnits, setAvailUomUnits] = useState<UOMUnitFull[]>([]);

  interface ItemFormData {
    number: string;
    name: string;
    revision: string;
    catalog_name?: string;
    item_type: string;
    uom_type_id: string;
    purchase_uom_id: string;
    storage_uom_id: string;
    sales_uom_id: string;
    preferred_bin_id: string;
    default_purchase_price: number;
    default_sales_price: number;
    track_lots: boolean;
    item_expires: boolean;
    item_requires_inspection: boolean;
    minimum_stock: number;
    maximum_stock: number;
    notes: string;
    description: string;
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<ItemFormData>({
    resolver: yupResolver(itemSchema) as any,
    defaultValues: {
      number: "",
      name: "",
      revision: "",
      catalog_name: undefined,
      item_type: "",
      uom_type_id: "",
      purchase_uom_id: "",
      storage_uom_id: "",
      sales_uom_id: "",
      default_purchase_price: 0,
      default_sales_price: 0,
      track_lots: true,
      item_expires: false,
      item_requires_inspection: false,
      minimum_stock: 0,
      maximum_stock: 1000,
      notes: "",
      description: "",
    },
  });

  // Watch UOM type changes to filter units
  const watchedUomTypeId = watch("uom_type_id");

  // Update selectedUomTypeId when watchedUomTypeId changes
  useEffect(() => {
    if (watchedUomTypeId && watchedUomTypeId !== selectedUomTypeId) {
      setSelectedUomTypeId(watchedUomTypeId);
      setValue("purchase_uom_id", "");
      setValue("sales_uom_id", "");
      setValue("storage_uom_id", "");
    }
  }, [watchedUomTypeId, selectedUomTypeId, uomUnits, setValue]);

  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/inventory/items/form?mode=view&id=${id}`);
  };

  const switchToEditMode = () => {
    navigate(`/inventory/items/form?mode=edit&id=${id}`);
  };

  const switchToCreateMode = () => {
    navigate("/inventory/items/form?mode=create");
  };

  useEffect(() => {
    if (itemData && (isEditMode || isViewMode)) {
      setSelectedUomTypeId(itemData.uom_type.id);
      setAvailUomUnits(
        uomUnits.filter((u) => u.uom_type_id === itemData.uom_type.id)
      );
      reset({
        number: itemData.number,
        name: itemData.name,
        revision: itemData.revision,
        catalog_name: itemData.catalog_name,
        item_type: itemData.item_type,
        uom_type_id: itemData.uom_type.id,
        purchase_uom_id: itemData.purchase_uom.id,
        storage_uom_id: itemData.storage_uom.id,
        sales_uom_id: itemData.sales_uom.id,
        default_purchase_price: parseFloat(itemData.default_purchase_price),
        default_sales_price: parseFloat(itemData.default_sales_price),
        track_lots: itemData.track_lots,
        item_expires: itemData.item_expires,
        item_requires_inspection: itemData.item_requires_inspection,
        minimum_stock: parseFloat(itemData.minimum_stock),
        maximum_stock: parseFloat(itemData.maximum_stock),
        notes: itemData.notes || "",
        description: itemData.description || "",
      });
    }
  }, [itemData, isEditMode, isViewMode, uomUnits, reset]);

  const onSubmit = async (data: ItemFormData) => {
    try {
      setError(null);

      if (isCreateMode) {
        const itemPayload: ItemCreate = {
          number: data.number,
          name: data.name,
          catalog_name: data.catalog_name,
          revision: data.revision,
          item_type: data.item_type as ItemType,
          uom_type_id: data.uom_type_id,
          purchase_uom_id: data.purchase_uom_id,
          storage_uom_id: data.storage_uom_id,
          sales_uom_id: data.sales_uom_id,
          preferred_bin_id: data.preferred_bin_id,
          default_purchase_price: data.default_purchase_price || 0,
          default_sales_price: data.default_sales_price || 0,
          track_lots: data.track_lots ?? true,
          item_expires: data.item_expires ?? false,
          item_requires_inspection: data.item_requires_inspection ?? false,
          minimum_stock: data.minimum_stock || 0,
          maximum_stock: data.maximum_stock || 0,
          notes: data.notes || undefined,
          description: data.description || undefined,
        };
        const new_itm = await createItemMutation.mutateAsync(itemPayload);
        navigate(`/inventory/items/form?mode=view&id=${new_itm.id}`);
      } else if (isEditMode) {
        if (!id) {
          throw new Error("Item ID is required for update");
        }
        const itemPayload: ItemUpdate = {
          id: id,
          number: data.number,
          name: data.name,
          catalog_name: data.catalog_name,
          revision: data.revision,
          item_type: data.item_type as ItemType,
          uom_type_id: data.uom_type_id,
          purchase_uom_id: data.purchase_uom_id,
          storage_uom_id: data.storage_uom_id,
          sales_uom_id: data.sales_uom_id,
          default_purchase_price: data.default_purchase_price || 0,
          default_sales_price: data.default_sales_price || 0,
          track_lots: data.track_lots,
          item_expires: data.item_expires ?? false,
          item_requires_inspection: data.item_requires_inspection ?? false,
          minimum_stock: data.minimum_stock || 0,
          maximum_stock: data.maximum_stock || 0,
          notes: data.notes || undefined,
          description: data.description || undefined,
        };
        await updateItemMutation.mutateAsync({ id: id, data: itemPayload });
        navigate(`/inventory/items/form?mode=view&id=${id}`);
      }
    } catch (err: any) {
      console.error("Failed to save item:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save item"
      );
    }
  };

  const handleCancel = () => {
    navigate("/inventory/items");
  };

  const isLoading = loadingItem || loadingUOM;
  const isSaving = createItemMutation.isPending || updateItemMutation.isPending;

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleCancel}>
          Back to Items
        </Button>
        <Typography variant="h4" component="h1">
          {isCreateMode
            ? "Create New Item"
            : isViewMode
            ? `View Item: ${itemData?.name || ""}`
            : `Edit Item: ${itemData?.name || ""}`}
        </Typography>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={(e) => {
            console.log(getValues());
          }}
        >
          DEBUG PRINT
        </Button>
      </Stack>

      {/* Error Alert */}
      {(error || itemError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || itemError?.message || "An unkown error occurred"}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Part Number *"
                    fullWidth
                    error={!!errors.number}
                    helperText={
                      errors.number?.message ||
                      "Unique part number (e.g., ABC-1234)"
                    }
                    disabled={isViewMode}
                  />
                )}
              />
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Item Name *"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
              <Controller
                name="catalog_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Catalog Name"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Box>
            <Controller
              name="item_type"
              control={control}
              render={({ field }) => (
                <FormControl disabled={isViewMode}>
                  <InputLabel>Item Type *</InputLabel>
                  <Select {...field} label="Item Type *">
                    {ITEM_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {stringUtils.capFirstLetter(type.replace(/[_]/g, " "))}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.item_type && (
                    <FormHelperText>{errors.item_type.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Controller
              name="preferred_bin_id"
              control={control}
              render={({ field }) => (
                <FormControl disabled={isViewMode}>
                  <InputLabel>Preferred BIN *</InputLabel>
                  <Select {...field} label="Preferred BIN *">
                    {locs.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.preferred_bin_id && (
                    <FormHelperText>
                      {errors.preferred_bin_id.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={isViewMode}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            {/* Unit of Measure Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Unit of Measure
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="uom_type_id"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.uom_type_id}
                    disabled={isViewMode}
                  >
                    <InputLabel>UOM Type *</InputLabel>
                    <Select
                      {...field}
                      label="UOM Type *"
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedUomTypeId(e.target.value);
                        // Clear UOM unit selections when type changes
                        // setValue("purchase_uom_id", "");
                        // setValue("storage_uom_id", "");
                        // setValue("sales_uom_id", "");
                        setAvailUomUnits(
                          uomUnits.filter(
                            (unit) => unit.uom_type_id === e.target.value
                          )
                        );
                      }}
                    >
                      {uomTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.type_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.uom_type_id && (
                      <FormHelperText>
                        {errors.uom_type_id.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="purchase_uom_id"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.purchase_uom_id}
                    disabled={isViewMode || !selectedUomTypeId}
                  >
                    <InputLabel>Purchase UOM *</InputLabel>
                    <Select {...field} label="Purchase UOM *">
                      {availUomUnits.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.unit_symbol} - {unit.unit_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.purchase_uom_id && (
                      <FormHelperText>
                        {errors.purchase_uom_id.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="storage_uom_id"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.storage_uom_id}
                    disabled={isViewMode || !selectedUomTypeId}
                  >
                    <InputLabel>Storage UOM *</InputLabel>
                    <Select {...field} label="Storage UOM *">
                      {availUomUnits.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.unit_symbol} - {unit.unit_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.storage_uom_id && (
                      <FormHelperText>
                        {errors.storage_uom_id.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="sales_uom_id"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.sales_uom_id}
                    disabled={isViewMode || !selectedUomTypeId}
                  >
                    <InputLabel>Sales UOM *</InputLabel>
                    <Select {...field} label="Sales UOM *">
                      {availUomUnits.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.unit_symbol} - {unit.unit_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.sales_uom_id && (
                      <FormHelperText>
                        {errors.sales_uom_id.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            {/* Pricing */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Pricing
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="default_purchase_price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Standard Cost"
                    type="number"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.default_purchase_price}
                    helperText={errors.default_purchase_price?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                )}
              />
              <Controller
                name="default_sales_price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="List Price"
                    type="number"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.default_sales_price}
                    helperText={errors.default_sales_price?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                )}
              />
            </Box>

            {/* Inventory Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Inventory Settings
            </Typography>

            <Box sx={{ display: "column", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="track_lots"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Track Lots"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  Enable lot tracking for FIFO inventory management
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="item_expires"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Item Expires?"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  Flag to indicate that this item Expires and requires
                  Expiration Date handling.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Controller
                  name="item_requires_inspection"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={isViewMode}
                        />
                      }
                      label="Additional IQC?"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary">
                  Flag to indicate that this item requires additional IQC when
                  received.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Controller
                name="minimum_stock"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Minimum Stock"
                    type="number"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.minimum_stock}
                    helperText={errors.minimum_stock?.message}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                )}
              />
              <Controller
                name="maximum_stock"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Maximum Stock"
                    type="number"
                    fullWidth
                    disabled={isViewMode}
                    error={!!errors.maximum_stock}
                    helperText={errors.maximum_stock?.message}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                )}
              />
            </Box>

            {/* Status and Notes */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Status and Notes
            </Typography>

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isViewMode}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                  placeholder="Additional notes about this item..."
                />
              )}
            />

            {/* Form Actions */}
            {!isViewMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={isSubmitting || isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSaving ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  disabled={isSubmitting || isSaving}
                >
                  {isSaving
                    ? "Saving..."
                    : isCreateMode
                    ? "Create Item"
                    : "Update Item"}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
